import React, { useState, useCallback, useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Download,
  FileText,
  Loader2,
  Save,
  Copy,
  Share2,
  Mail,
  ExternalLink,
  FilePlus,
  Clock,
  History,
  ChevronUp,
  ChevronDown,
  BarChart3,
  Users,
  FileStack,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Import document components
import {
  ChecklistItem,
  SortableImage,
  CropModal,
  EmailDialog,
  PDFPreview,
  TemplateManager,
  InspectorManager,
  StatisticsDashboard,
  CHECKLIST_ITEMS,
  API,
} from "@/components/document";
import {
  ensureApiAvailable,
  fetchApi,
  getApiStatusSnapshot,
  getFriendlyApiMessage,
  requestApiJson,
} from "@/lib/api-client";

// Main Document Generator Component
const DocumentGenerator = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  
  // Helper function to ensure data is serializable (avoids postMessage clone errors)
  const serializeData = (data) => {
    try {
      return JSON.parse(JSON.stringify(data));
    } catch (e) {
      console.warn('Daten-Serialisierung Warnung:', e);
      return data;
    }
  };

  const [images, setImages] = useState([]);
  const [date, setDate] = useState(new Date().toLocaleDateString("de-DE"));
  const [description, setDescription] = useState("Objekt: ");
  const [imagesPerPage, setImagesPerPage] = useState(4);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440
  );
  const [previewAreaWidth, setPreviewAreaWidth] = useState(0);
  const [cropImage, setCropImage] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [checklistEnabled, setChecklistEnabled] = useState(true);
  const [checklist, setChecklist] = useState(() => {
    const initial = {};
    CHECKLIST_ITEMS.forEach(item => { initial[item] = false; });
    return initial;
  });
  const [title, setTitle] = useState("Dokumentation Objektbegehung");
  const [maintenanceNotes, setMaintenanceNotes] = useState("");
  const [inspector, setInspector] = useState("");
  const [savedDocumentId, setSavedDocumentId] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [visibleRecentDocuments, setVisibleRecentDocuments] = useState(6);
  const [retentionDays, setRetentionDays] = useState(60);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  
  // New feature states
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showInspectorManager, setShowInspectorManager] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    ...getApiStatusSnapshot(),
    state: "checking",
    message: "API wird geprüft…",
  });
  
  const fileInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const previewAreaRef = useRef(null);

  // Helper function to parse and adjust date
  const adjustDate = (days) => {
    let currentDate;
    // Try to parse DD.MM.YYYY format
    const parts = date.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (parts) {
      currentDate = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
    } else {
      currentDate = new Date();
    }
    currentDate.setDate(currentDate.getDate() + days);
    const newDate = `${currentDate.getDate().toString().padStart(2, '0')}.${(currentDate.getMonth() + 1).toString().padStart(2, '0')}.${currentDate.getFullYear()}`;
    setDate(newDate);
  };

  // Calculate page count automatically based on images and imagesPerPage
  const pageCount = Math.max(1, Math.ceil(images.length / imagesPerPage)) || 1;

  // Load recent documents on mount
  useEffect(() => {
    loadRecentDocuments();
    loadDescriptionSuggestions();
  }, []);

  useEffect(() => {
    let isActive = true;

    const runApiHealthCheck = async () => {
      try {
        const status = await ensureApiAvailable();
        if (isActive) {
          setApiStatus(status);
        }
      } catch (error) {
        if (isActive) {
          setApiStatus({
            ...getApiStatusSnapshot(),
            state: "error",
            message: getFriendlyApiMessage(error),
          });
        }
      }
    };

    runApiHealthCheck();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setPreviewAreaWidth(previewAreaRef.current?.clientWidth || 0);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load document if documentId is provided
  useEffect(() => {
    if (documentId) {
      loadDocument(documentId);
    }
  }, [documentId]);

  const loadDescriptionSuggestions = async () => {
    try {
      const data = await requestApiJson("/suggestions/descriptions");
      setDescriptionSuggestions(JSON.parse(JSON.stringify(data?.suggestions || [])));
    } catch (error) {
      console.log("Fehler beim Laden der Vorschläge:", error);
    }
  };

  const loadRecentDocuments = async () => {
    try {
      const data = await requestApiJson("/documents/recent?limit=30");
      setRecentDocuments(JSON.parse(JSON.stringify(data?.documents || [])));
      setVisibleRecentDocuments(6);
      setRetentionDays(Number(data?.retention_days) || 60);
    } catch (error) {
      console.log("Fehler beim Laden der letzten Dokumente:", error);
    }
  };

  const loadDocument = async (id) => {
    setIsLoading(true);
    try {
      const doc = await requestApiJson(`/documents/${id}`);
      
      // Serialize all values to avoid postMessage clone errors
      setTitle(String(doc.title || "Dokumentation Objektbegehung"));
      setDate(String(doc.date || ''));
      setDescription(String(doc.description || ''));
      setImages(JSON.parse(JSON.stringify(doc.images || [])));
      setImagesPerPage(Number(doc.images_per_page) || 4);
      setChecklistEnabled(Boolean(doc.checklist_enabled !== false));
      setChecklist(JSON.parse(JSON.stringify(doc.checklist || {})));
      setMaintenanceNotes(String(doc.maintenance_notes || ""));
      setInspector(String(doc.inspector || ""));
      setSavedDocumentId(String(doc.id));
      
      toast.success("Dokument geladen");
    } catch (error) {
      toast.error(getFriendlyApiMessage(error));
      navigate("/");
    }
    setIsLoading(false);
  };

  const saveDocument = async () => {
    setIsSaving(true);
    try {
      // Clean and serialize images to avoid postMessage clone errors
      const cleanImages = images.map(img => ({
        id: String(img.id),
        data: String(img.data),
        caption: String(img.caption || ''),
        rotation: Number(img.rotation || 0),
      }));

      // Ensure all data is serializable - use primitives only
      const docData = {
        title: String(title || ''),
        date: String(date || ''),
        description: String(description || ''),
        images: cleanImages,
        images_per_page: Number(imagesPerPage || 4),
        checklist_enabled: Boolean(checklistEnabled),
        checklist: JSON.parse(JSON.stringify(checklist || {})),
        maintenance_notes: String(maintenanceNotes || ''),
        inspector: String(inspector || ''),
      };

      // Serialize entire payload to ensure no non-cloneable objects
      const serializedPayload = JSON.parse(JSON.stringify(docData));

      // Calculate approximate payload size
      const payloadSize = JSON.stringify(serializedPayload).length;
      console.log(`Saving document with payload size: ${(payloadSize / 1024 / 1024).toFixed(2)} MB`);

      const path = savedDocumentId 
        ? `/documents/${savedDocumentId}`
        : "/documents";

      const result = await requestApiJson(path, {
        method: savedDocumentId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(serializedPayload),
      });

      setSavedDocumentId(String(result.id));
      
      if (!documentId) {
        navigate(`/dokument/${result.id}`, { replace: true });
      }
      
      // Reload recent documents list and description suggestions
      loadRecentDocuments();
      loadDescriptionSuggestions();
      
      toast.success(savedDocumentId ? "Dokument aktualisiert" : "Dokument gespeichert");
      return String(result.id);
    } catch (error) {
      console.error("Save error:", error);
      let errorMsg = getFriendlyApiMessage(error);
      if (error.message.includes("413")) {
        errorMsg = "Daten zu groß - Bitte weniger Bilder verwenden";
      } else if (error.message.includes("520") || error.message.includes("502") || error.message.includes("503")) {
        errorMsg = "Server nicht erreichbar - Bitte später erneut versuchen";
      }

      setApiStatus({
        ...getApiStatusSnapshot(),
        state: "error",
        message: errorMsg,
      });

      toast.error(errorMsg);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const copyDocumentLink = () => {
    if (savedDocumentId) {
      const link = `${window.location.origin}/dokument/${savedDocumentId}`;
      navigator.clipboard.writeText(link);
      toast.success("Link kopiert!");
    }
  };

  const shareEditLink = async () => {
    // First save the document if not already saved
    if (!savedDocumentId) {
      await saveDocument();
    }
    if (savedDocumentId) {
      const link = `${window.location.origin}/dokument/${savedDocumentId}`;
      navigator.clipboard.writeText(link);
      toast.success("Bearbeitungs-Link kopiert!");
    }
  };

  const sendPdfByEmail = async ({ email, subject, message }) => {
    setIsSendingEmail(true);

    try {
      // First save the document if not already saved
      let docId = savedDocumentId;
      if (!docId) {
        // Serialize all data to avoid postMessage clone errors
        const savePayload = JSON.parse(JSON.stringify({
          title: String(title || ''),
          date: String(date || ''),
          description: String(description || ''),
          images: images.map(img => ({
            id: String(img.id),
            data: String(img.data),
            caption: String(img.caption || ''),
            rotation: Number(img.rotation || 0),
          })),
          images_per_page: Number(imagesPerPage || 4),
          checklist_enabled: Boolean(checklistEnabled),
          checklist: checklist || {},
          maintenance_notes: String(maintenanceNotes || ''),
          inspector: String(inspector || ''),
        }));
        
        const result = await requestApiJson("/documents", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(savePayload),
        });

        if (result?.id) {
          docId = String(result.id);
          setSavedDocumentId(docId);
          navigate(`/dokument/${docId}`, { replace: true });
        }
      }

      // Serialize email payload
      const emailPayload = JSON.parse(JSON.stringify({
        email: String(email),
        subject: String(subject),
        message: String(message),
        pdf_data: {
          title: String(title || ''),
          date: String(date || ''),
          description: String(description || ''),
          images: images.map((img) => ({
            id: String(img.id),
            data: String(img.data),
            caption: String(img.caption || ''),
            rotation: Number(img.rotation || 0),
          })),
          images_per_page: Number(imagesPerPage || 4),
          checklist_enabled: Boolean(checklistEnabled),
          checklist: checklist || {},
          maintenance_notes: String(maintenanceNotes || ''),
          inspector: String(inspector || ''),
          document_id: docId ? String(docId) : null,
        },
      }));

      const result = await requestApiJson("/send-pdf-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailPayload),
      });

      toast.success(result.message || "E-Mail erfolgreich gesendet!");
      setShowEmailDialog(false);
    } catch (error) {
      toast.error(`Fehler: ${getFriendlyApiMessage(error)}`);
    }

    setIsSendingEmail(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const moved = arrayMove(items, oldIndex, newIndex);
        // Serialize to avoid postMessage clone errors
        return JSON.parse(JSON.stringify(moved));
      });
    }
  };

  const handleFileUpload = async (files) => {
    const validFiles = Array.from(files).filter(
      (file) => file.type === "image/jpeg" || file.type === "image/png"
    );

    if (validFiles.length !== files.length) {
      toast.error("Nur JPG und PNG Dateien sind erlaubt");
    }

    if (validFiles.length === 0) return;

    setIsLoading(true);
    setUploadProgress({ current: 0, total: validFiles.length, fileName: '' });

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadProgress({ current: i + 1, total: validFiles.length, fileName: file.name });

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetchApi("/upload-image", {
          method: "POST",
          body: formData,
        });

        const data = await response.json();
        
        // Use functional update to avoid stale closure issues
        setImages((prev) => {
          const newImage = {
            id: String(data.id),
            data: String(data.data),
            caption: "",
            rotation: 0,
          };
          // Deep clone to ensure serializability
          return [...prev, JSON.parse(JSON.stringify(newImage))];
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`${file.name}: ${error.message}`);
      }
    }

    setUploadProgress({ current: 0, total: 0, fileName: '' });
    setIsLoading(false);
    if (validFiles.length > 0) {
      toast.success(`${validFiles.length} Bild(er) hochgeladen`);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleRotate = (id) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id 
          ? JSON.parse(JSON.stringify({ ...img, rotation: (img.rotation + 90) % 360 }))
          : img
      )
    );
  };

  const handleDelete = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    toast.success("Bild gelöscht");
  };

  const handleCaptionChange = (id, caption) => {
    setImages((prev) =>
      prev.map((img) => (img.id === id 
        ? JSON.parse(JSON.stringify({ ...img, caption }))
        : img))
    );
  };

  const handleCropSave = (id, croppedData) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id 
          ? JSON.parse(JSON.stringify({ ...img, data: croppedData, rotation: 0 }))
          : img
      )
    );
    toast.success("Bild zugeschnitten");
  };

  const toggleChecklistItem = (item) => {
    setChecklist((prev) => {
      const updated = { ...prev, [item]: !prev[item] };
      return JSON.parse(JSON.stringify(updated));
    });
  };

  // Handler for applying a template
  const handleApplyTemplate = (templateConfig) => {
    setTitle(templateConfig.title || "Dokumentation Objektbegehung");
    setChecklistEnabled(templateConfig.checklistEnabled);
    // Serialize to avoid postMessage clone errors
    setChecklist(JSON.parse(JSON.stringify(templateConfig.checklist || {})));
  };

  // Handler for selecting an inspector
  const handleSelectInspector = (inspectorName) => {
    setInspector(inspectorName);
  };

  const createNewDocument = () => {
    // Reset all state to defaults
    setTitle("Dokumentation Objektbegehung");
    setImages([]);
    setDate(new Date().toLocaleDateString("de-DE"));
    setDescription("");
    setImagesPerPage(4);
    setChecklistEnabled(true);
    setChecklist({});
    setMaintenanceNotes("");
    setInspector("");
    setSavedDocumentId(null);
    navigate("/", { replace: true });
    toast.success("Neues Dokument erstellt");
  };

  const generatePDF = async () => {
    setIsLoading(true);

    try {
      let docId = savedDocumentId;

      if (!docId) {
        docId = await saveDocument();
      }

      if (!docId) {
        throw new Error("Dokument konnte vor der PDF-Erstellung nicht gespeichert werden.");
      }

      const response = await fetchApi(`/documents/${docId}/generate-pdf`, {
        method: "POST",
      });

      // Get document ID from response header first (before reading body)
      const newDocumentId = response.headers.get("X-Document-Id");

      // Update document ID if new
      if (newDocumentId && newDocumentId !== savedDocumentId) {
        setSavedDocumentId(String(newDocumentId));
        if (!savedDocumentId) {
          navigate(`/dokument/${newDocumentId}`, { replace: true });
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileDocId = newDocumentId || docId;
      a.download = `Dokumentation_Objektbegehung_${date.replace(/\./g, "-")}_${fileDocId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("PDF erstellt und Dokument gespeichert!");
    } catch (error) {
      toast.error(`Fehler: ${getFriendlyApiMessage(error)}`);
    }

    setIsLoading(false);
  };

  const checkedCount = Object.values(checklist).filter(Boolean).length;
  const displayedRecentDocuments = recentDocuments.slice(0, visibleRecentDocuments);
  const canShowMoreRecentDocuments = visibleRecentDocuments < Math.min(recentDocuments.length, 30);
  const effectivePreviewZoom = Math.max(
    0.38,
    Math.min(0.9, ((previewAreaWidth || viewportWidth) - 32) / 794)
  );

  return (
    <div className="app-container" data-testid="document-generator">
      {/* Header */}
      <header className="app-header">
        <div className="header-top">
          <div className="header-title">
            <FileText className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <h1 style={{ fontFamily: 'Manrope, sans-serif' }}>PDF Dokumentation Objektbegehung</h1>
          </div>
          {savedDocumentId && (
            <div className="document-id-badge">
              <span className="text-xs text-green-700">ID: {savedDocumentId}</span>
              <button
                onClick={copyDocumentLink}
                className="p-1 hover:bg-green-100 rounded"
                title="Link kopieren"
              >
                <Copy className="w-3 h-3 text-green-600" />
              </button>
            </div>
          )}
        </div>
        <div className="header-buttons">
          <Button
            onClick={createNewDocument}
            variant="outline"
            className="header-btn rounded-full"
            data-testid="new-document-btn"
          >
            <FilePlus className="w-4 h-4 mr-2" />
            <span className="btn-text">Neu</span>
          </Button>
          
          <Button
            onClick={saveDocument}
            disabled={isSaving}
            variant="outline"
            className="header-btn rounded-full"
            data-testid="save-document-btn"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 spinner" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            <span className="btn-text">{savedDocumentId ? "Aktualisieren" : "Speichern"}</span>
          </Button>
          
          {/* Share Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="header-btn rounded-full" data-testid="share-dropdown-btn">
                <Share2 className="w-4 h-4 mr-2" />
                <span className="btn-text">Teilen</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={shareEditLink} data-testid="share-edit-link">
                <ExternalLink className="w-4 h-4 mr-2" />
                Bearbeitungs-Link kopieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setShowEmailDialog(true)}
                data-testid="share-email-pdf"
              >
                <Mail className="w-4 h-4 mr-2" />
                PDF per E-Mail senden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Tools Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="header-btn rounded-full" data-testid="tools-dropdown-btn">
                <FileStack className="w-4 h-4 mr-2" />
                <span className="btn-text">Extras</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setShowTemplateManager(true)} data-testid="open-templates">
                <FileStack className="w-4 h-4 mr-2" />
                Vorlagen verwalten
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowInspectorManager(true)} data-testid="open-inspectors">
                <Users className="w-4 h-4 mr-2" />
                Prüfer verwalten
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowStatistics(true)} data-testid="open-statistics">
                <BarChart3 className="w-4 h-4 mr-2" />
                Statistiken
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={generatePDF}
            disabled={isLoading}
            className="header-btn header-btn-primary rounded-full"
            data-testid="generate-pdf-btn"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 spinner" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            <span className="btn-text">PDF Erstellen</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-content">
        {/* Left Sidebar - Document Settings */}
        <aside className="sidebar" data-testid="settings-sidebar">
          <div className="sidebar-content">
            <h2>Dokument-Einstellungen</h2>

            {/* Title Field - First */}
            <div className="form-field">
              <Label htmlFor="title-input">Titel</Label>
              <Input
                id="title-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Dokumentation Objektbegehung"
                data-testid="title-input"
              />
            </div>

            {/* Date Field with Arrows */}
            <div className="form-field">
              <Label htmlFor="date-input">Datum</Label>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  onClick={() => adjustDate(-1)}
                  title="Tag zurück"
                  data-testid="date-prev-btn"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Input
                  id="date-input"
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="TT.MM.JJJJ"
                  className="text-center"
                  data-testid="date-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  onClick={() => adjustDate(1)}
                  title="Tag vor"
                  data-testid="date-next-btn"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
              </div>
            </div>

              <div className="form-field">
                <Label htmlFor="description-input">Objekt</Label>
                <div className="relative">
                  <Input
                    ref={descriptionInputRef}
                    id="description-input"
                    type="text"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="z.B. Musterstraße 123, Berlin"
                    autoComplete="off"
                    data-testid="description-input"
                  />
                  {/* Autocomplete suggestions */}
                  {showSuggestions && description && descriptionSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {descriptionSuggestions
                        .filter(s => s.toLowerCase().includes(description.toLowerCase()) && s.toLowerCase() !== description.toLowerCase())
                        .slice(0, 8)
                        .map((suggestion, idx) => (
                          <button
                            key={idx}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 border-b last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDescription(suggestion);
                              setShowSuggestions(false);
                            }}
                          >
                            {suggestion}
                          </button>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>

              {/* Checklist Toggle */}
              <div className="form-field mt-6">
                <div className="flex items-center justify-between mb-2">
                  <Label>Prüfliste anzeigen</Label>
                  <button
                    onClick={() => setChecklistEnabled(!checklistEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      checklistEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    data-testid="checklist-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        checklistEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Checklist Section */}
              {checklistEnabled && (
                <div className="form-field">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Prüfliste ({checkedCount}/{CHECKLIST_ITEMS.length})</Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        const allChecked = Object.values(checklist).every(Boolean);
                        const newState = {};
                        CHECKLIST_ITEMS.forEach(item => { newState[item] = !allChecked; });
                        setChecklist(JSON.parse(JSON.stringify(newState)));
                      }}
                      className="text-xs"
                      data-testid="toggle-all-checklist"
                    >
                      Alle {Object.values(checklist).every(Boolean) ? 'abwählen' : 'auswählen'}
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto bg-gray-50 p-2 rounded border">
                    {CHECKLIST_ITEMS.map((item) => (
                      <ChecklistItem
                        key={item}
                        item={item}
                        isChecked={checklist[item] === true || (typeof checklist[item] === 'string' && checklist[item])}
                        onToggle={() => toggleChecklistItem(item)}
                        selectedOption={checklist[item]}
                        onSelectOption={(option) => {
                          setChecklist(prev => JSON.parse(JSON.stringify({
                            ...prev,
                            [item]: option || false
                          })));
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Image Upload Section - After Checklist */}
              <div className="form-field mt-6">
                <Label>Bilder ({images.length})</Label>
                
                {/* Upload Progress Bar */}
                {uploadProgress.total > 0 && (
                  <div className="mt-2 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-blue-700">
                        Hochladen: {uploadProgress.current} von {uploadProgress.total}
                      </span>
                      <span className="text-xs text-blue-600">
                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-1 truncate">{uploadProgress.fileName}</p>
                  </div>
                )}
                
                {/* Upload Area */}
                <div
                  className={`upload-area mt-2 ${isDragOver ? "active" : ""} ${uploadProgress.total > 0 ? "opacity-50 pointer-events-none" : ""}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => !uploadProgress.total && fileInputRef.current?.click()}
                  data-testid="image-upload-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">
                    Bilder hier ablegen
                  </p>
                  <p className="text-xs text-muted-foreground">
                    oder klicken zum Auswählen (JPG, PNG)
                  </p>
                </div>

                {/* Image Pool with Drag & Drop */}
                {images.length > 0 && (
                  <div className="mt-3">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={images.map((img) => img.id)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="image-pool" data-testid="image-pool">
                          <AnimatePresence>
                            {images.map((image) => (
                              <SortableImage
                                key={image.id}
                                image={image}
                                onRotate={handleRotate}
                                onCrop={(img) => setCropImage(img)}
                                onDelete={handleDelete}
                                onCaptionChange={handleCaptionChange}
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>

              {/* Maintenance Notes */}
              <div className="form-field mt-4">
                <Label htmlFor="maintenance-input">
                  Instandhaltungs-/Instandsetzungsmaßnahmen
                </Label>
                <Textarea
                  id="maintenance-input"
                  value={maintenanceNotes}
                  onChange={(e) => setMaintenanceNotes(e.target.value)}
                  placeholder="Laufende oder abgeschlossene Maßnahmen, Priorisierung..."
                  rows={3}
                  className="text-sm"
                  data-testid="maintenance-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Erscheint nur im PDF, wenn Text eingegeben wird
                </p>
              </div>

              {/* Inspector */}
              <div className="form-field">
                <Label htmlFor="inspector-input">Prüfer</Label>
                <Input
                  id="inspector-input"
                  value={inspector}
                  onChange={(e) => setInspector(e.target.value)}
                  placeholder="Name des Prüfers"
                  data-testid="inspector-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Erscheint nur im PDF, wenn Name eingegeben wird
                </p>
              </div>

            </div>
        </aside>

        {/* Center - PDF Preview */}
        <main className="preview-area" data-testid="preview-area" ref={previewAreaRef}>
          <PDFPreview
            images={images}
            date={date}
            description={description}
            imagesPerPage={imagesPerPage}
            pageCount={pageCount}
            zoom={effectivePreviewZoom}
            checklistEnabled={checklistEnabled}
            checklist={checklist}
            maintenanceNotes={maintenanceNotes}
            inspector={inspector}
            title={title}
          />
        </main>

        {/* Right Sidebar - Info & Recent Documents (Desktop only) */}
        <aside className="sidebar-right-info hidden lg:block" data-testid="info-sidebar">
          {(apiStatus.state === "error" || !apiStatus.isUsingConfiguredOrigin) && (
            <div
              className={`p-3 border rounded-lg mb-4 ${
                apiStatus.state === "error"
                  ? "bg-red-50 border-red-200"
                  : "bg-sky-50 border-sky-200"
              }`}
              data-testid="api-status-banner"
            >
              <p
                className={`text-xs ${
                  apiStatus.state === "error" ? "text-red-700" : "text-sky-700"
                }`}
                data-testid="api-status-message"
              >
                {apiStatus.message}
              </p>
            </div>
          )}

          {/* Retention Notice */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Hinweis:</strong> Dokumente werden nach {retentionDays} Tagen automatisch gelöscht.
              </p>
            </div>
          </div>

          {/* Recent Documents */}
          {recentDocuments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Letzte Begehungen</span>
              </div>
              <div className="space-y-2">
                {displayedRecentDocuments.map((doc) => (
                  <a
                    key={doc.id}
                    href={`/dokument/${doc.id}`}
                    className={`block p-2.5 text-xs rounded-lg border transition-colors ${
                      savedDocumentId === doc.id 
                        ? 'bg-blue-50 border-blue-300' 
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                    data-testid={`recent-doc-${doc.id}`}
                  >
                    <div className="font-medium truncate mb-1">{doc.description || 'Ohne Objekt'}</div>
                    <div className="flex justify-between items-center text-muted-foreground">
                      <span>{doc.date}</span>
                      <span className={`${doc.days_remaining <= 7 ? 'text-red-500 font-medium' : ''}`}>
                        {doc.days_remaining}d
                      </span>
                    </div>
                  </a>
                ))}

                {canShowMoreRecentDocuments && (
                  <button
                    type="button"
                    className="text-xs text-sky-700 hover:text-sky-900 underline underline-offset-2"
                    onClick={() => setVisibleRecentDocuments((prev) => Math.min(prev + 3, 30, recentDocuments.length))}
                    data-testid="show-more-recent-documents"
                  >
                    mehr anzeigen
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Crop Modal */}
      <CropModal
        isOpen={!!cropImage}
        onClose={() => setCropImage(null)}
        image={cropImage}
        onSave={handleCropSave}
      />

      {/* Email Dialog */}
      <EmailDialog
        isOpen={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        onSend={sendPdfByEmail}
        isLoading={isSendingEmail}
        description={description}
        date={date}
        documentId={savedDocumentId}
      />

      {/* Template Manager */}
      <TemplateManager
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        onApplyTemplate={handleApplyTemplate}
        currentConfig={{
          title,
          checklistEnabled,
          checklist,
        }}
      />

      {/* Inspector Manager */}
      <InspectorManager
        isOpen={showInspectorManager}
        onClose={() => setShowInspectorManager(false)}
        onSelectInspector={handleSelectInspector}
        currentInspector={inspector}
      />

      {/* Statistics Dashboard */}
      <StatisticsDashboard
        isOpen={showStatistics}
        onClose={() => setShowStatistics(false)}
      />

      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<DocumentGenerator />} />
          <Route path="/dokument/:documentId" element={<DocumentGenerator />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
